import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  RefreshCw,
} from 'lucide-react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { checkServerHealth } from '../utils/serverHealth';

const TimeoutErrorPage = () => {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    const checkServer = async () => {
      const isServerUp = await checkServerHealth();
      if (isServerUp) {
        navigate('/dashboard');
      } else {
        console.log('Server still slow or unreachable...');
      }
    };

    const interval = setInterval(checkServer, 10000);
    return () => clearInterval(interval);
  }, [navigate]);

  const handleManualCheck = async () => {
    setIsChecking(true);
    const isServerUp = await checkServerHealth();
    if (isServerUp) {
      navigate('/dashboard');
    } else {
      console.log('Manual check: Still timing out');
    }
    setTimeout(() => setIsChecking(false), 1000);
  };

  return (
    <Box sx={{
      padding: '32px',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8fafc'
    }}>
      <Box sx={{
        maxWidth: '600px',
        width: '100%',
        textAlign: 'center',
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: '24px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
        border: '1px solid #e2e8f0'
      }}>
        <Box sx={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#fff7ed',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <Clock size={40} color="#f97316" />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', mb: 2 }}>
          Request Timeout
        </Typography>
        
        <Typography sx={{ color: '#64748b', mb: 4, lineHeight: 1.6 }}>
          The server is taking too long to respond. This could be due to a slow connection or high server load.
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          gap: '16px', 
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          <Button
            variant="contained"
            startIcon={isChecking ? <CircularProgress size={20} color="inherit" /> : <RefreshCw size={20} />}
            onClick={handleManualCheck}
            disabled={isChecking}
            sx={{
              backgroundColor: '#f97316',
              '&:hover': { backgroundColor: '#ea580c' },
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            {isChecking ? 'Checking...' : 'Try Again'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={() => window.location.reload()}
            sx={{
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f1f5f9' },
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Refresh Page
          </Button>
        </Box>

        <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #f1f5f9' }}>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            We'll automatically redirect you as soon as the connection is restored.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TimeoutErrorPage;
